import { createContext, useContext, useEffect, useState } from 'react';
import userSettingsEngine from '../controllers/UserSettings/UserSettingsEngine';
import { OpenToast } from '../JxControls/JxToasts/StyledToast';
import { subscribe } from '../services/utils/pubsub';
import { events } from '../services/wss/SystemEvents';

const UserSettingsContext = createContext(null);

/**
 * Provides resolved user settings to the component tree.
 * Reads defaults immediately — re-renders once when bootup completes via pubsub.
 * No bootup call here — PageContent owns that.
 */
export function UserSettingsProvider({ children }) {
    const [settings, setSettings] = useState(() => userSettingsEngine.resolveAll());

    useEffect(() => {
        const unsub = subscribe(events.onUserSettingsLoaded, () => {
            setSettings(userSettingsEngine.resolveAll());
        });
        return unsub;
    }, []);

    // ADDED — global hotkey 'i' toggles tooltip.enabled from anywhere. Guarded against
    // input/textarea/select/contentEditable so it never interferes with typing.
    // More global hotkeys can join this same effect as they come up.
    useEffect(() => {
        function handleGlobalHotkey(e) {
            const tag = document.activeElement?.tagName?.toLowerCase();
            if (['input', 'textarea', 'select'].includes(tag) || document.activeElement?.isContentEditable) return;
            if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;

            if (e.key === 'i' || e.key === 'I') {
                setSettings((prev) => {
                    const next = !prev['tooltip.enabled'];
                    userSettingsEngine.set('tooltip.enabled', next);
                    OpenToast(next ? 'Tooltips enabled' : 'Tooltips disabled');
                    return { ...prev, 'tooltip.enabled': next };
                });
            }
        }
        window.addEventListener('keydown', handleGlobalHotkey);
        return () => window.removeEventListener('keydown', handleGlobalHotkey);
    }, []);

    /**
     * Updates a single setting in the engine cache and triggers a re-render.
     * @param {string} key
     * @param {*} value
     */
    const setSetting = (key, value) => {
        // updates cache + persists to BE
        userSettingsEngine.set(key, value);

        // patch context state — re-renders consumers
        setSettings((prev) => ({ ...prev, [key]: value }));
    };

    return <UserSettingsContext.Provider value={{ settings, setSetting }}>{children}</UserSettingsContext.Provider>;
}

/**
 * Returns a single resolved setting value — reactive via context.
 * @param {string} key
 * @returns {*}
 */
export function useUserSetting(key) {
    const ctx = useContext(UserSettingsContext);
    if (!ctx) throw new Error('useUserSetting must be used within UserSettingsProvider');
    return ctx.settings[key];
}

/**
 * Returns setSetting — use in SettingsTab to persist + propagate changes.
 * @returns {(key: string, value: *) => void}
 */
export function useSetUserSetting() {
    const ctx = useContext(UserSettingsContext);
    if (!ctx) throw new Error('useSetUserSetting must be used within UserSettingsProvider');
    return ctx.setSetting;
}

export default UserSettingsContext;
